import { Box } from "@mui/material";
import type { FC } from "react";
import MetaChip from "@/ui/Badges/MetaChip";
import { EventListItem } from "@/ui/molecules/EventListItem";
import type { LedgerEntry } from "@/utils/models";
import { RequestOriginHppdMeter } from "./RequestOriginHppdMeter";
import { RequestOriginMergedHistory } from "./RequestOriginMergedHistory";
import { RequestOriginTile } from "./RequestOriginTile";
import { batchLabel, deltaLabel, formatAt, kindTitle, leadShort, type OriginCopyContext } from "./requestOriginCopy";
import { hppdNumbersOf } from "./requestOriginFacts";
import { DELTA_CHIP_TONE } from "./requestOriginMeta";
import { sentence } from "./requestOriginSentence";

type Props = { entry: LedgerEntry; isLatest: boolean; copy: OriginCopyContext; testId: string };

/** One step of a request's story: what changed its slots, who or what, when, and the slots it opened or closed. */
export const RequestOriginLedgerEntryRow: FC<Props> = ({ entry, isLatest, copy, testId }) => {
	const hppd = hppdNumbersOf(entry);
	const batch = batchLabel(entry, copy);
	const timing = [entry.at === null ? null : formatAt(entry.at, copy), entry.lead_minutes === null ? null : leadShort(entry.lead_minutes, copy)].filter(
		(value): value is string => value !== null,
	);
	return (
		<EventListItem
			dataTestId={testId}
			emphasis={isLatest}
			leading={<RequestOriginTile entry={entry} />}
			title={kindTitle(entry, copy)}
			text={
				<>
					{sentence(entry, true, copy)}
					{hppd !== null && (
						<Box sx={{ mt: 1 }}>
							<RequestOriginHppdMeter numbers={hppd} copy={copy} dataTestId={`${testId}-meter`} />
						</Box>
					)}
					<RequestOriginMergedHistory entry={entry} copy={copy} testId={testId} />
				</>
			}
			meta={
				timing.length > 0 || batch !== null ? (
					<Box component="span" sx={{ display: "inline-flex", flexWrap: "wrap", alignItems: "center", columnGap: 0.75, rowGap: 0.5 }}>
						{timing.length > 0 && <span>{timing.join(" · ")}</span>}
						{batch !== null && <MetaChip size="small" tone="neutral" label={batch} dataTestId={`${testId}-batch`} />}
					</Box>
				) : undefined
			}
			trailing={
				entry.delta === 0 ? undefined : (
					<MetaChip
						size="small"
						tone={entry.delta > 0 ? DELTA_CHIP_TONE.OPENED : DELTA_CHIP_TONE.REMOVED}
						label={deltaLabel(entry.delta, copy)}
						dataTestId={`${testId}-delta`}
					/>
				)
			}
		/>
	);
};
